import { COMMAND_CENTER_ROOT, commandCenterPath } from './commandCenterRoutes'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../api/client'
import {
  PortalDetail, usePortal, portalModulePath, portalContextPath, portalRepoPath, humanizePortalError,
  createAction, updateAction, createNote, createBlocker, updateBlocker,
  attachModule, updatePortal, createReceipt, setPortalProjectLink,
  setModulePublished, recipientPreviewPath, RecipientAccessPanel,
  PORTAL_FACTORY_ROOT, PORTAL_REGISTRY_ROOT,
  type ActionStatus, type ActionPriority, type BlockerSeverity, type ReadinessStatus,
} from './olivium/portal-factory'

/**
 * Portal detail route wrapper (reopen a saved portal).
 *
 *   /square/command-center/olivium/portals/:portalId
 *
 * Owns usePortal + performs the persisted mutations, then reloads so the UI
 * always reflects server truth (never an optimistic fake). An inherited
 * module's "Open surface" navigates to the existing V2 spine route.
 */
export default function SquareCodePortalDetailRoute() {
  const navigate = useNavigate()
  const { portalId } = useParams<{ portalId: string }>()
  const state = usePortal(portalId)
  const id = portalId || ''
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([])
  const [projectsError, setProjectsError] = useState<string | null>(null)

  // Load the operator's canonical projects for the link selector. Non-fatal to
  // the portal, but the failure is surfaced honestly (never collapsed into a
  // false "you have no projects" state) so the link card can say so.
  useEffect(() => {
    let cancelled = false
    void api.projects
      .list()
      .then((r) => { if (cancelled) return; setProjects((r.projects || []).map((p) => ({ id: p.id, name: p.name }))); setProjectsError(null) })
      .catch((e) => { if (!cancelled) setProjectsError(humanizePortalError(e)) })
    return () => { cancelled = true }
  }, [])

  // Always reload server truth AND surface a rejected mutation honestly.
  // Resolves to true on success / false on failure so an inline input only
  // clears on a real save — a failed write is never a silent no-op.
  const after = (p: Promise<unknown>): Promise<boolean> =>
    p.then(
      () => { setMutationError(null); return state.reload().then(() => true) },
      (e) => { setMutationError(humanizePortalError(e)); return state.reload().then(() => false) },
    )

  return (
    <PortalDetail
      detail={state.detail}
      loading={state.loading}
      error={state.error}
      authRequired={state.authRequired}
      notFound={state.notFound}
      mutationError={mutationError}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onOpenFactory={() => navigate(PORTAL_FACTORY_ROOT)}
      onOpenRegistry={() => navigate(PORTAL_REGISTRY_ROOT)}
      onOpenModule={(moduleKey) => navigate(portalModulePath(id, moduleKey))}
      onOpenLink={(url) => navigate(url)}
      onOpenContext={() => navigate(portalContextPath(id))}
      onOpenRepo={() => navigate(portalRepoPath(id))}
      onIssueAccess={() => {
        // PHASE_2M P7 — prefill the Secure Access issue form for THIS portal.
        const q = new URLSearchParams({
          target_type: 'project_portal', target_id: id,
          label: state.detail ? `Portal access — ${state.detail.portal.project_name}` : 'Portal access',
        })
        navigate(`${COMMAND_CENTER_ROOT}/olivium/secure-access/new?${q.toString()}`)
      }}
      onOpenSecureAccess={() => navigate(commandCenterPath('olivium/secure-access'))}
      onCreateAction={(input: { title: string; priority: ActionPriority }) => after(createAction(id, input))}
      onCycleAction={(actionId: string, status: ActionStatus) => after(updateAction(id, actionId, { status }))}
      onCreateNote={(body: string) => after(createNote(id, body))}
      onCreateBlocker={(input: { summary: string; severity: BlockerSeverity }) => after(createBlocker(id, input))}
      onResolveBlocker={(blockerId: string, resolution: string) => after(updateBlocker(id, blockerId, { status: 'resolved', resolution }))}
      onAttachModule={(moduleKey: string) => after(attachModule(id, moduleKey))}
      onSetReadiness={(status: ReadinessStatus) => after(updatePortal(id, { readiness_status: status }))}
      onCreateReceipt={(summary: string) => after(createReceipt(id, summary))}
      projects={projects}
      projectsError={projectsError}
      onSetProjectLink={(project_id: string | null) => after(setPortalProjectLink(id, project_id))}
      onOpenProject={(project_id: string) => navigate(`/square/projects/${project_id}`)}
      onSetModulePublished={(module, published) => after(setModulePublished(id, module, published))}
      onPreviewRecipient={() => navigate(recipientPreviewPath(id))}
      recipientAccess={
        state.detail ? (
          <RecipientAccessPanel
            portalId={id}
            linked={Boolean(state.detail.linked_project)}
            portalName={state.detail.portal.project_name}
          />
        ) : null
      }
    />
  )
}
